<template>
  <div class="container mx-auto flex h-full min-h-0 flex-col overflow-hidden px-4 py-4">
    <div v-if="loading" class="py-20 text-center">
      <div class="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-teal-500"></div>
    </div>

    <div
      v-else-if="loadError"
      class="rounded-lg border border-red-800 bg-red-900/20 p-8 text-center"
    >
      <p class="text-sm text-red-300">{{ loadError }}</p>
      <router-link to="/events" class="mt-4 inline-block text-sm text-teal-400 hover:underline">
        Back to events
      </router-link>
    </div>

    <template v-else>
      <PageHeading class="mb-6 shrink-0" :title="pageTitle" :crumbs="eventCrumbs">
        <template #title-extra>
          <span
            :class="getEventTypeBadgeClass(form.eventType)"
            class="shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold"
          >
            {{ getEventTypeLabel(form.eventType) }}
          </span>
        </template>
        <template #actions>
          <PageSecondaryButton class="min-w-32" data-testid="cancel-event" @click="handleCancel">
            Cancel
          </PageSecondaryButton>
          <PagePrimaryButton
            class="min-w-32"
            type="submit"
            form="event-form"
            data-testid="save-event"
            :disabled="saving"
          >
            <i class="pi pi-save shrink-0 text-base" aria-hidden="true" />
            {{ saving ? "Saving..." : "Save" }}
          </PagePrimaryButton>
        </template>
      </PageHeading>

      <!-- The card fills the space under the heading so its height stays put while switching sections -->
      <form
        id="event-form"
        class="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-700 bg-gray-900"
        @submit.prevent="handleSave"
      >
        <p
          v-if="serverError"
          data-testid="server-error"
          class="mx-6 mt-4 shrink-0 rounded-md border border-red-800 bg-red-900/20 p-3 text-sm text-red-400"
        >
          {{ serverError }}
        </p>

        <div class="flex min-h-0 flex-1 flex-col md:flex-row">
          <!-- Section menu -->
          <nav
            aria-label="Event settings"
            class="shrink-0 border-b border-gray-700 md:w-1/3 md:shrink-0 md:overflow-y-auto md:border-b-0 md:border-r"
          >
            <div class="flex gap-1 overflow-x-auto p-2 md:flex-col md:p-3">
              <button
                v-for="section in sections"
                :key="section.id"
                type="button"
                :data-testid="`section-${section.id}`"
                :aria-current="section.id === activeSection ? 'true' : undefined"
                class="flex shrink-0 items-start gap-3 rounded-md px-3 py-2.5 text-left transition-colors md:w-full md:shrink"
                :class="section.id === activeSection ? 'bg-teal-500/10' : 'hover:bg-gray-800/70'"
                @click="goToSection(section.id)"
              >
                <i
                  class="pi mt-0.5 text-sm"
                  :class="[
                    section.icon,
                    section.id === activeSection ? 'text-teal-300' : 'text-gray-500'
                  ]"
                ></i>
                <span class="min-w-0 flex-1">
                  <span class="flex items-center gap-2">
                    <span
                      class="text-sm font-medium"
                      :class="section.id === activeSection ? 'text-white' : 'text-gray-300'"
                    >
                      {{ section.label }}
                    </span>
                    <span
                      v-if="sectionErrorCounts[section.id]"
                      :data-testid="`section-error-${section.id}`"
                      class="h-2 w-2 shrink-0 rounded-full bg-red-500"
                      :title="`${sectionErrorCounts[section.id]} field${sectionErrorCounts[section.id] !== 1 ? 's' : ''} need attention`"
                    ></span>
                  </span>
                  <span class="mt-0.5 hidden truncate text-xs text-gray-500 md:block">
                    {{ section.summary }}
                  </span>
                </span>
              </button>
            </div>
          </nav>

          <!-- Active section -->
          <section class="min-h-0 min-w-0 flex-1 overflow-y-auto p-6">
            <header class="mb-6 border-b border-gray-800 pb-4">
              <h2 class="text-lg font-semibold text-white">{{ activeSectionMeta.label }}</h2>
              <p class="mt-1 text-sm text-gray-400">{{ activeSectionMeta.description }}</p>
            </header>

            <!-- General -->
            <div v-if="activeSection === 'general'" class="space-y-6">
              <div>
                <label for="event-name" class="block text-sm font-medium text-gray-300">
                  Name <span class="text-red-500">*</span>
                </label>
                <input
                  id="event-name"
                  v-model="form.name"
                  type="text"
                  :class="[fieldClass, errors.name ? 'border-red-500' : '']"
                  placeholder="Enter event name"
                />
                <p v-if="errors.name" data-testid="error-name" class="mt-1 text-xs text-red-400">
                  {{ errors.name }}
                </p>
                <p v-else class="mt-1 text-xs text-gray-500">
                  Name is shown on the header area of the voting page.
                </p>
              </div>

              <div>
                <label for="event-slug" class="block text-sm font-medium text-gray-300">
                  Event Slug <span v-if="!isNew" class="text-red-500">*</span>
                </label>
                <input
                  id="event-slug"
                  v-model="form.slug"
                  type="text"
                  :class="[fieldClass, 'font-mono', errors.slug ? 'border-red-500' : '']"
                  placeholder="event-slug-example"
                />
                <p v-if="errors.slug" data-testid="error-slug" class="mt-1 text-xs text-red-400">
                  {{ errors.slug }}
                </p>
                <p v-else class="mt-1 text-xs text-gray-500">
                  Used in the event URL. Only lowercase letters, numbers, and hyphens allowed.
                </p>
              </div>
            </div>

            <!-- Schedule -->
            <div
              v-else-if="activeSection === 'schedule'"
              class="grid grid-cols-1 gap-6 xl:grid-cols-2"
            >
              <template v-if="isSetlistAllowed(form.eventType)">
                <div>
                  <label
                    for="event-start-time"
                    class="mb-1 block text-sm font-medium text-gray-300"
                  >
                    Event Start Time <span class="text-red-500">*</span>
                  </label>
                  <DatePicker
                    id="event-start-time"
                    v-model="form.eventStartTime"
                    show-time
                    hour-format="24"
                    :show-icon="true"
                    date-format="yy-mm-dd"
                    :step-minute="1"
                    class="w-full"
                    :input-class="fieldClass"
                  />
                  <p
                    v-if="errors.eventStartTime"
                    data-testid="error-eventStartTime"
                    class="mt-1 text-xs text-red-400"
                  >
                    {{ errors.eventStartTime }}
                  </p>
                  <p v-else class="mt-1 text-xs text-gray-500">
                    When the live event begins. Does not affect voting availability.
                  </p>
                </div>

                <div>
                  <label for="event-end-time" class="mb-1 block text-sm font-medium text-gray-300">
                    Event End Time <span class="text-red-500">*</span>
                  </label>
                  <DatePicker
                    id="event-end-time"
                    v-model="form.eventEndTime"
                    show-time
                    hour-format="24"
                    :show-icon="true"
                    date-format="yy-mm-dd"
                    :step-minute="1"
                    class="w-full"
                    :input-class="fieldClass"
                  />
                  <p
                    v-if="errors.eventEndTime"
                    data-testid="error-eventEndTime"
                    class="mt-1 text-xs text-red-400"
                  >
                    {{ errors.eventEndTime }}
                  </p>
                  <p v-else class="mt-1 text-xs text-gray-500">
                    When the event ends. After this time, the event page shows as ended and voting
                    is no longer possible.
                  </p>
                </div>
              </template>

              <div>
                <label
                  for="event-voting-start"
                  class="mb-1 block text-sm font-medium text-gray-300"
                >
                  <template v-if="isKaraoke(form.eventType)">Event Start Time</template>
                  <template v-else>Voting Start Time</template>
                  <span class="text-red-500">*</span>
                </label>
                <DatePicker
                  id="event-voting-start"
                  v-model="form.votingStartTime"
                  show-time
                  hour-format="24"
                  :show-icon="true"
                  date-format="yy-mm-dd"
                  :step-minute="1"
                  class="w-full"
                  :input-class="fieldClass"
                />
                <p
                  v-if="errors.votingStartTime"
                  data-testid="error-votingStartTime"
                  class="mt-1 text-xs text-red-400"
                >
                  {{ errors.votingStartTime }}
                </p>
                <p v-else class="mt-1 text-xs text-gray-500">
                  <template v-if="isKaraoke(form.eventType)">
                    When the karaoke session begins.
                  </template>
                  <template v-else>Can be set before event start to allow early voting.</template>
                </p>
              </div>

              <div>
                <label for="event-voting-end" class="mb-1 block text-sm font-medium text-gray-300">
                  <template v-if="isKaraoke(form.eventType)">Event End Time</template>
                  <template v-else>Voting End Time</template>
                  <span v-if="isPrevoting(form.eventType)" class="text-red-500">*</span>
                </label>
                <DatePicker
                  id="event-voting-end"
                  v-model="form.votingEndTime"
                  show-time
                  hour-format="24"
                  :show-icon="true"
                  date-format="yy-mm-dd"
                  :step-minute="1"
                  class="w-full"
                  :input-class="fieldClass"
                />
                <p
                  v-if="errors.votingEndTime"
                  data-testid="error-votingEndTime"
                  class="mt-1 text-xs text-red-400"
                >
                  {{ errors.votingEndTime }}
                </p>
                <p v-else class="mt-1 text-xs text-gray-500">
                  <template v-if="isKaraoke(form.eventType)"
                    >When the karaoke session ends.</template
                  >
                  <template v-else-if="isPrevoting(form.eventType)">
                    Required. Defines when pre-voting closes.
                  </template>
                  <template v-else>
                    Optional. If not set, voting stays open until manually disabled or event ends.
                  </template>
                </p>
              </div>
            </div>

            <!-- Details -->
            <div v-else-if="activeSection === 'details'" class="space-y-6">
              <div>
                <label for="event-theme" class="block text-sm font-medium text-gray-300"
                  >Theme</label
                >
                <Select
                  id="event-theme"
                  v-model="form.theme"
                  :options="themeOptions"
                  option-label="label"
                  option-value="value"
                  placeholder="Select theme"
                  class="mt-1 w-full"
                  :show-clear="false"
                />
                <p class="mt-1 text-xs text-gray-500">
                  Define the 'default' theme of the voting page when users open it.
                </p>
              </div>

              <div>
                <label for="event-concerts-style" class="block text-sm font-medium text-gray-300">
                  Concerts page style
                </label>
                <Select
                  id="event-concerts-style"
                  v-model="form.concertsStyle"
                  :options="concertsStyleOptions"
                  option-label="label"
                  option-value="value"
                  placeholder="Select style"
                  class="mt-1 w-full"
                  :show-clear="false"
                />
                <p class="mt-1 text-xs text-gray-500">
                  Visual style for the public concerts page, including screen and LED views.
                </p>
              </div>

              <div v-if="isKaraoke(form.eventType)">
                <label for="event-signup-mode" class="mb-1 block text-sm font-medium text-gray-300">
                  Karaoke Sign-Up Mode
                </label>
                <Select
                  id="event-signup-mode"
                  v-model="form.signUpMode"
                  :options="signUpModeOptions"
                  option-label="label"
                  option-value="value"
                  placeholder="Select sign-up mode"
                  class="w-full"
                />
                <p class="mt-1 text-xs text-gray-500">
                  Controls how performers can sign up for karaoke slots.
                </p>
              </div>

              <div>
                <label for="event-notes" class="block text-sm font-medium text-gray-300"
                  >Notes</label
                >
                <textarea
                  id="event-notes"
                  v-model="form.notes"
                  rows="3"
                  :class="fieldClass"
                  placeholder="Enter optional notes"
                ></textarea>
                <p class="mt-1 text-xs text-gray-500">
                  Internal notes for admin use only. Not visible to the audience.
                </p>
              </div>
            </div>

            <!-- Language -->
            <div v-else-if="activeSection === 'language'" class="space-y-6">
              <div>
                <label for="event-enabled-locales" class="block text-sm font-medium text-gray-300">
                  Languages on event page
                </label>
                <MultiSelect
                  id="event-enabled-locales"
                  v-model="form.enabledLocales"
                  :options="localeOptions"
                  option-label="label"
                  option-value="value"
                  placeholder="Select languages for the language switcher"
                  display="chip"
                  class="mt-1 w-full"
                />
                <p v-if="form.enabledLocales.length === 0" class="mt-1 text-xs text-gray-500">
                  No selection: English will be used and the language switcher will be hidden.
                </p>
                <p v-else class="mt-1 text-xs text-gray-500">
                  Customers can switch between the selected languages on the event page.
                </p>
              </div>

              <div v-if="form.enabledLocales.length > 0">
                <label for="event-default-locale" class="block text-sm font-medium text-gray-300">
                  Default language
                </label>
                <Select
                  id="event-default-locale"
                  v-model="form.defaultLocale"
                  :options="defaultLocaleOptions"
                  option-label="label"
                  option-value="value"
                  placeholder="Select default language"
                  class="mt-1 w-full"
                  :show-clear="false"
                />
                <p class="mt-1 text-xs text-gray-500">
                  Language shown when customers open the event page via QR code or direct link.
                </p>
              </div>
            </div>

            <!-- Song management -->
            <div v-else-if="activeSection === 'songs'" class="space-y-8">
              <div
                v-if="!isPrevoting(form.eventType) && !isKaraoke(form.eventType)"
                class="flex items-start justify-between gap-4"
              >
                <div>
                  <label for="event-auto-queue" class="block text-sm font-medium text-gray-300">
                    Auto Queue Next Song
                  </label>
                  <p class="mt-1 text-xs text-gray-500">
                    Automatically locks the next highest-voted song as "Up Next" when a song starts
                    performing
                  </p>
                </div>
                <ToggleSwitch
                  id="event-auto-queue"
                  v-model="form.autoQueueNext"
                  :class="['shrink-0', form.autoQueueNext ? 'event-auto-queue-toggle' : '']"
                />
              </div>

              <div>
                <label
                  for="event-discarded-categories"
                  class="block text-sm font-medium text-gray-300"
                >
                  Exclude categories
                </label>
                <MultiSelect
                  id="event-discarded-categories"
                  v-model="form.discardedCategories"
                  :options="categories"
                  option-label="name"
                  option-value="name"
                  placeholder="Select categories to exclude from this event"
                  display="chip"
                  :filter="true"
                  class="mt-1 w-full"
                  :loading="categoriesLoading"
                />
                <p class="mt-1 text-xs text-gray-500">
                  All songs in selected categories will be hidden. If a song has multiple
                  categories, it is hidden when any one of them is excluded.
                </p>
              </div>

              <div>
                <label for="event-discarded-songs" class="block text-sm font-medium text-gray-300">
                  Exclude individual songs
                </label>
                <MultiSelect
                  id="event-discarded-songs"
                  v-model="form.discardedSongs"
                  :options="songsForIndividualExclusion"
                  option-label="title"
                  option-value="id"
                  placeholder="Select additional songs to remove from this event's voting list"
                  display="chip"
                  :filter="true"
                  class="mt-1 w-full"
                  :loading="songsLoading"
                >
                  <template #option="{ option }">
                    <div class="flex flex-col">
                      <span class="font-medium">{{ option.title }}</span>
                      <span class="text-xs text-gray-500">{{ option.author }}</span>
                    </div>
                  </template>
                </MultiSelect>
                <p class="mt-1 text-xs text-gray-500">
                  For one-off exclusions, including uncategorized songs. Songs already excluded by
                  category are not shown here.
                </p>
                <p v-if="exclusionSummaryText" class="mt-2 text-xs font-medium text-blue-400">
                  {{ exclusionSummaryText }}
                </p>
              </div>

              <div v-if="!isKaraoke(form.eventType)" data-testid="pinned-songs">
                <label for="event-pin-song" class="block text-sm font-medium text-gray-300">
                  Pin to the top of the playlist
                </label>
                <p class="mt-1 text-xs text-gray-500">
                  These songs stay at the top of the playlist, in this order, even with no votes.
                  People can still vote for them.
                </p>
                <ul v-if="pinnedSongRows.length > 0" class="mt-3 space-y-2">
                  <li
                    v-for="(song, index) in pinnedSongRows"
                    :key="song.id"
                    class="flex items-center gap-2 rounded-md border border-gray-700 bg-gray-800/60 px-3 py-2"
                  >
                    <span class="w-5 text-sm tabular-nums text-gray-400">{{ index + 1 }}</span>
                    <span class="min-w-0 flex-1">
                      <span class="block truncate text-sm font-medium text-white">{{
                        song.title
                      }}</span>
                      <span v-if="song.author" class="block truncate text-xs text-gray-500">{{
                        song.author
                      }}</span>
                    </span>
                    <button
                      type="button"
                      class="p-1 text-gray-400 transition-colors hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
                      title="Move up"
                      :data-testid="`pinned-move-up-${song.id}`"
                      :disabled="index === 0"
                      @click="movePinnedSong(index, -1)"
                    >
                      <i class="pi pi-arrow-up" style="font-size: 14px"></i>
                    </button>
                    <button
                      type="button"
                      class="p-1 text-gray-400 transition-colors hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
                      title="Move down"
                      :data-testid="`pinned-move-down-${song.id}`"
                      :disabled="index === pinnedSongRows.length - 1"
                      @click="movePinnedSong(index, 1)"
                    >
                      <i class="pi pi-arrow-down" style="font-size: 14px"></i>
                    </button>
                    <button
                      type="button"
                      class="p-1 text-red-400 transition-colors hover:text-red-300"
                      title="Remove pin"
                      :data-testid="`pinned-remove-${song.id}`"
                      @click="removePinnedSong(song.id)"
                    >
                      <i class="pi pi-times" style="font-size: 14px"></i>
                    </button>
                  </li>
                </ul>
                <p v-else class="mt-3 text-xs text-gray-500">No songs pinned.</p>
                <div class="mt-3 flex items-start gap-2">
                  <Select
                    id="event-pin-song"
                    v-model="pinSongToAdd"
                    :options="songsAvailableToPin"
                    option-label="title"
                    option-value="id"
                    placeholder="Choose a song to pin"
                    data-testid="pin-song-select"
                    class="min-w-0 flex-1"
                    :filter="true"
                    :show-clear="true"
                    :loading="songsLoading"
                  />
                  <button
                    type="button"
                    data-testid="add-pinned-song"
                    class="shrink-0 rounded-md border border-teal-600 px-3 py-2 text-sm font-medium text-teal-400 transition-colors hover:bg-teal-900/20 hover:text-teal-300 disabled:cursor-not-allowed disabled:opacity-40"
                    :disabled="pinSongToAdd == null || pinSongToAdd === ''"
                    @click="addPinnedSong(pinSongToAdd)"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            <!-- Setlist -->
            <div v-else-if="activeSection === 'setlist'" class="space-y-8">
              <div class="flex items-start justify-between gap-4">
                <div>
                  <label
                    for="event-setlist-enabled"
                    class="block text-sm font-medium text-gray-300"
                  >
                    Enable predefined setlist length
                  </label>
                  <p class="mt-1 text-xs text-gray-500">
                    Shows a "set end" marker in the voting list so the audience can see which songs
                    will fit into the current set.
                  </p>
                </div>
                <ToggleSwitch
                  id="event-setlist-enabled"
                  v-model="form.setlistEnabled"
                  :class="['shrink-0', form.setlistEnabled ? 'event-setlist-toggle' : '']"
                />
              </div>

              <div v-if="form.setlistEnabled" class="space-y-3">
                <label class="block text-sm font-medium text-gray-300"
                  >Set durations (minutes)</label
                >
                <div
                  v-for="(duration, index) in form.setlistDurations"
                  :key="index"
                  class="flex items-center gap-3"
                >
                  <span class="w-14 text-sm font-medium text-gray-400">Set {{ index + 1 }}</span>
                  <input
                    type="number"
                    :value="duration"
                    min="1"
                    max="300"
                    :class="[fieldClass, 'mt-0 w-24']"
                    placeholder="min"
                    @input="updateSetDuration(index, $event.target.value)"
                  />
                  <span class="text-sm text-gray-400">min</span>
                  <button
                    type="button"
                    class="p-1 text-red-400 transition-colors hover:text-red-300"
                    title="Remove this set"
                    :disabled="form.setlistDurations.length <= 1"
                    :class="{ 'cursor-not-allowed opacity-30': form.setlistDurations.length <= 1 }"
                    @click="removeSetDuration(index)"
                  >
                    <i class="pi pi-trash" style="font-size: 14px"></i>
                  </button>
                </div>
                <button
                  type="button"
                  class="flex items-center gap-2 rounded-md border border-dashed border-teal-600 px-3 py-2 text-sm font-medium text-teal-400 transition-colors hover:bg-teal-900/20 hover:text-teal-300"
                  @click="addSetDuration"
                >
                  <i class="pi pi-plus" style="font-size: 12px"></i>
                  Add set
                </button>
                <p class="text-xs text-gray-500">
                  Total: {{ setlistTotalMinutes }} minutes across
                  {{ form.setlistDurations.length }} set{{
                    form.setlistDurations.length !== 1 ? "s" : ""
                  }}
                </p>
              </div>
            </div>

            <!-- Controls -->
            <div v-else-if="activeSection === 'controls'" class="space-y-8">
              <div class="flex items-start justify-between gap-4">
                <div>
                  <label for="event-disabled" class="block text-sm font-medium text-gray-300">
                    Disable Event
                  </label>
                  <p class="mt-1 text-xs text-gray-500">
                    Closes the event and takes the landing page offline
                  </p>
                </div>
                <ToggleSwitch
                  id="event-disabled"
                  v-model="form.isDisabled"
                  :class="['shrink-0', form.isDisabled ? 'event-disabled-toggle' : '']"
                />
              </div>

              <div class="flex items-start justify-between gap-4">
                <div>
                  <label
                    for="event-voting-disabled"
                    class="block text-sm font-medium text-gray-300"
                  >
                    Disable Voting
                  </label>
                  <p class="mt-1 text-xs text-gray-500">
                    Prevents voting but keeps the event page accessible
                  </p>
                </div>
                <ToggleSwitch
                  id="event-voting-disabled"
                  v-model="form.isVotingDisabled"
                  :class="['shrink-0', form.isVotingDisabled ? 'event-voting-disabled-toggle' : '']"
                />
              </div>
            </div>
          </section>
        </div>
      </form>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { onBeforeRouteLeave, useRoute, useRouter } from "vue-router";
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";
import PageHeading from "@/components/common/PageHeading.vue";
import PagePrimaryButton from "@/components/common/PagePrimaryButton.vue";
import PageSecondaryButton from "@/components/common/PageSecondaryButton.vue";
import DatePicker from "primevue/datepicker";
import MultiSelect from "primevue/multiselect";
import Select from "primevue/select";
import ToggleSwitch from "primevue/toggleswitch";
import api from "@/services/api";
import { formatDateTime } from "@/utils/dateFormat";
import { countExcludedSongs, isSongExcludedForEvent } from "@/utils/eventSongFilter";
import { CONCERTS_STYLE_OPTIONS, CONCERTS_STYLES } from "@/constants/concertsStyles";
import { DEFAULT_LOCALE, LOCALE_OPTIONS } from "@/constants/locales";
import {
  EVENT_TYPES,
  getEventTypeBadgeClass,
  getEventTypeLabel,
  isKaraoke,
  isPrevoting,
  isSetlistAllowed,
  normalizeEventType
} from "@/constants/eventTypes";

defineOptions({ name: "EventForm" });

const route = useRoute();
const router = useRouter();
const toast = useToast();
const confirm = useConfirm();

const fieldClass =
  "mt-1 block w-full rounded-md border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white shadow-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500";

const SLUG_PATTERN = /^[a-z0-9-]+$/;

const themeOptions = [
  { label: "Light", value: "light" },
  { label: "Dark", value: "dark" }
];
const concertsStyleOptions = CONCERTS_STYLE_OPTIONS;
const signUpModeOptions = [
  { label: "Hidden (admin only)", value: "none" },
  { label: "Direct Sign-Up (Find Admin at Venue)", value: "direct" },
  { label: "QR Code Scanning (Generate QR, Show to Admin)", value: "qr" }
];
const localeOptions = LOCALE_OPTIONS;

function createEmptyEventForm(eventType = EVENT_TYPES.CONCERT) {
  return {
    name: "",
    slug: "",
    eventType,
    eventStartTime: null,
    eventEndTime: null,
    isDisabled: false,
    isVotingDisabled: false,
    votingStartTime: null,
    votingEndTime: null,
    discardedSongs: [],
    discardedCategories: [],
    pinnedSongs: [],
    notes: "",
    theme: "light",
    concertsStyle: CONCERTS_STYLES.DEFAULT,
    signUpMode: "none",
    defaultLocale: DEFAULT_LOCALE,
    enabledLocales: [],
    autoQueueNext: false,
    setlistEnabled: false,
    setlistDurations: eventType === EVENT_TYPES.CONCERT ? [45] : []
  };
}

function formFromEvent(event) {
  const parseDate = (value) => (value ? new Date(value) : null);
  return {
    name: event.name || "",
    slug: event.slug || "",
    eventType: normalizeEventType(event.eventType),
    eventStartTime: parseDate(event.eventStartTime),
    eventEndTime: parseDate(event.eventEndTime),
    isDisabled: event.isDisabled || false,
    isVotingDisabled: event.isVotingDisabled || false,
    votingStartTime: parseDate(event.votingStartTime),
    votingEndTime: parseDate(event.votingEndTime),
    discardedSongs: event.discardedSongs || [],
    discardedCategories: event.discardedCategories || [],
    pinnedSongs: event.pinnedSongs || [],
    notes: event.notes || "",
    theme: event.theme || "light",
    concertsStyle: event.concertsStyle || CONCERTS_STYLES.DEFAULT,
    signUpMode: event.signUpMode || "none",
    defaultLocale: event.defaultLocale || DEFAULT_LOCALE,
    enabledLocales: event.enabledLocales || [],
    autoQueueNext: event.autoQueueNext || false,
    setlistEnabled: event.setlistEnabled || false,
    setlistDurations:
      event.setlistDurations && event.setlistDurations.length > 0
        ? [...event.setlistDurations]
        : [45]
  };
}

const eventId = computed(() => route.params.id);
const isNew = computed(() => !eventId.value);

const form = ref(createEmptyEventForm(normalizeEventType(route.query.type)));
const loading = ref(false);
const loadError = ref("");
const saving = ref(false);
const serverError = ref("");
const errors = ref({});
const songs = ref([]);
const songsLoading = ref(false);
const pinSongToAdd = ref(null);
const categories = ref([]);
const categoriesLoading = ref(false);
const pristineSnapshot = ref("");
const leavingAfterSave = ref(false);

const pageTitle = computed(() => {
  if (isNew.value) return `Create ${getEventTypeLabel(form.value.eventType)} Event`;
  return form.value.name || "Edit Event";
});

const eventCrumbs = computed(() => [
  { label: "Events", to: "/events" },
  { label: pageTitle.value }
]);

const setlistTotalMinutes = computed(() =>
  form.value.setlistDurations.reduce((sum, d) => sum + (parseInt(d) || 0), 0)
);

const defaultLocaleOptions = computed(() =>
  localeOptions.filter((option) => form.value.enabledLocales.includes(option.value))
);

const songsForIndividualExclusion = computed(() => {
  const previewEvent = {
    discardedCategories: form.value.discardedCategories || [],
    discardedSongs: []
  };
  return songs.value.filter((song) => !isSongExcludedForEvent(song, previewEvent));
});

const eligibleSongsForPin = computed(() => {
  const previewEvent = {
    discardedCategories: form.value.discardedCategories || [],
    discardedSongs: form.value.discardedSongs || []
  };
  return songs.value.filter(
    (song) => !song.isDisabled && !isSongExcludedForEvent(song, previewEvent)
  );
});

const pinnedSongRows = computed(() => {
  const byId = new Map(songs.value.map((song) => [Number(song.id), song]));
  return (form.value.pinnedSongs || []).map((id) => byId.get(Number(id))).filter(Boolean);
});

const songsAvailableToPin = computed(() => {
  const pinned = new Set((form.value.pinnedSongs || []).map((id) => Number(id)));
  return eligibleSongsForPin.value.filter((song) => !pinned.has(Number(song.id)));
});

const songsSectionSummary = computed(() => {
  const parts = [];
  const pinCount = isKaraoke(form.value.eventType) ? 0 : (form.value.pinnedSongs || []).length;
  if (pinCount > 0) {
    parts.push(`${pinCount} pinned`);
  }
  if (exclusionSummaryText.value) {
    parts.push(exclusionSummaryText.value);
  }
  return parts.join(" · ") || "All songs available";
});

function addPinnedSong(songId) {
  const id = Number(songId);
  if (!Number.isFinite(id)) return;
  const current = form.value.pinnedSongs || [];
  if (current.some((existing) => Number(existing) === id)) return;
  form.value.pinnedSongs = [...current, id];
  pinSongToAdd.value = null;
}

function movePinnedSong(index, delta) {
  const next = [...(form.value.pinnedSongs || [])];
  const target = index + delta;
  if (target < 0 || target >= next.length) return;
  const [row] = next.splice(index, 1);
  next.splice(target, 0, row);
  form.value.pinnedSongs = next;
}

function removePinnedSong(songId) {
  form.value.pinnedSongs = (form.value.pinnedSongs || []).filter(
    (existing) => Number(existing) !== Number(songId)
  );
}

function prunePinnedSongs() {
  if (!songs.value.length) return;
  const allowed = new Set(eligibleSongsForPin.value.map((song) => Number(song.id)));
  const current = form.value.pinnedSongs || [];
  const next = current.filter((id) => allowed.has(Number(id)));
  if (
    next.length !== current.length ||
    next.some((id, index) => Number(id) !== Number(current[index]))
  ) {
    form.value.pinnedSongs = next;
  }
}

const exclusionSummaryText = computed(() => {
  const stats = countExcludedSongs(songs.value, {
    discardedCategories: form.value.discardedCategories || [],
    discardedSongs: form.value.discardedSongs || []
  });
  if (stats.total === 0) return null;

  const parts = [`${stats.total} song${stats.total !== 1 ? "s" : ""} excluded`];
  const catCount = (form.value.discardedCategories || []).length;
  if (catCount > 0) {
    parts.push(`${catCount} categor${catCount !== 1 ? "ies" : "y"}`);
  }
  if (stats.individualOnly > 0) {
    parts.push(`${stats.individualOnly} individual`);
  }
  return parts.join(" · ");
});

const scheduleSummary = computed(() => {
  const usesEventTimes = isSetlistAllowed(form.value.eventType);
  const start = usesEventTimes ? form.value.eventStartTime : form.value.votingStartTime;
  const end = usesEventTimes ? form.value.eventEndTime : form.value.votingEndTime;
  if (!start) return "Not scheduled";
  return end ? `${formatDateTime(start)} - ${formatDateTime(end)}` : formatDateTime(start);
});

const detailsSummary = computed(() => {
  const style = concertsStyleOptions.find((option) => option.value === form.value.concertsStyle);
  const parts = [style?.label || "Default style"];
  if (form.value.notes.trim()) {
    parts.push("notes added");
  }
  return parts.join(" - ");
});

const languageSummary = computed(() => {
  const count = form.value.enabledLocales.length;
  if (count === 0) return "English only";
  return `${count} language${count !== 1 ? "s" : ""} - default ${form.value.defaultLocale.toUpperCase()}`;
});

const controlsSummary = computed(() => {
  const off = [];
  if (form.value.isDisabled) off.push("Event disabled");
  if (form.value.isVotingDisabled) off.push("Voting disabled");
  return off.length > 0 ? off.join(", ") : "Event and voting live";
});

const sections = computed(() => {
  const items = [
    {
      id: "general",
      label: "General",
      icon: "pi-pencil",
      description: "Name and the public URL the audience opens.",
      summary: form.value.name || "Unnamed event"
    },
    {
      id: "schedule",
      label: "Schedule",
      icon: "pi-calendar",
      description: "When the event runs and when voting is open.",
      summary: scheduleSummary.value
    },
    {
      id: "details",
      label: "Details",
      icon: "pi-info-circle",
      description: "How the event page looks, plus notes for your own reference.",
      summary: detailsSummary.value
    },
    {
      id: "language",
      label: "Language",
      icon: "pi-language",
      description: "Languages offered on the voting page.",
      summary: languageSummary.value
    },
    {
      id: "songs",
      label: "Song Management",
      icon: "pi-play-circle",
      description: isKaraoke(form.value.eventType)
        ? "Which songs the audience can vote for."
        : "Which songs the audience can vote for, and which stay at the top of the playlist.",
      summary: songsSectionSummary.value
    }
  ];

  if (isSetlistAllowed(form.value.eventType)) {
    items.push({
      id: "setlist",
      label: "Setlist",
      icon: "pi-list-check",
      description: "Set lengths behind the set end marker.",
      summary: form.value.setlistEnabled
        ? `${form.value.setlistDurations.length} set${form.value.setlistDurations.length !== 1 ? "s" : ""} - ${setlistTotalMinutes.value} min`
        : "Disabled"
    });
  }

  items.push({
    id: "controls",
    label: "Controls",
    icon: "pi-cog",
    description: "Take the event or voting offline.",
    summary: controlsSummary.value
  });

  return items;
});

const activeSection = computed(() => {
  const requested = route.query.section;
  return sections.value.some((section) => section.id === requested) ? requested : "general";
});

const activeSectionMeta = computed(
  () => sections.value.find((section) => section.id === activeSection.value) || sections.value[0]
);

const SECTION_BY_FIELD = {
  name: "general",
  slug: "general",
  eventStartTime: "schedule",
  eventEndTime: "schedule",
  votingStartTime: "schedule",
  votingEndTime: "schedule"
};

const sectionErrorCounts = computed(() => {
  const counts = {};
  Object.keys(errors.value).forEach((field) => {
    const section = SECTION_BY_FIELD[field];
    counts[section] = (counts[section] || 0) + 1;
  });
  return counts;
});

const goToSection = (section) => {
  router.replace({ query: { ...route.query, section } });
};

function validateEventForm() {
  const found = {};
  const slug = form.value.slug.trim();

  if (!form.value.name.trim()) {
    found.name = "Event name is required.";
  }
  if (!isNew.value && !slug) {
    found.slug = "Event slug is required.";
  } else if (slug && !SLUG_PATTERN.test(slug)) {
    found.slug = "Only lowercase letters, numbers, and hyphens are allowed.";
  }
  if (isSetlistAllowed(form.value.eventType)) {
    if (!form.value.eventStartTime) {
      found.eventStartTime = "Event start time is required.";
    }
    if (!form.value.eventEndTime) {
      found.eventEndTime = "Event end time is required.";
    }
  }
  if (!form.value.votingStartTime) {
    found.votingStartTime = isKaraoke(form.value.eventType)
      ? "Event start time is required."
      : "Voting start time is required.";
  }
  if (isPrevoting(form.value.eventType) && !form.value.votingEndTime) {
    found.votingEndTime = "Voting end time is required for pre-voting events.";
  }

  return found;
}

function buildPayload() {
  const formatDateForAPI = (date) => {
    if (!date) return null;
    return date instanceof Date ? date.toISOString() : date;
  };

  const payload = {
    name: form.value.name.trim(),
    slug: form.value.slug.trim() || null,
    eventType: form.value.eventType,
    eventStartTime: formatDateForAPI(form.value.eventStartTime),
    eventEndTime: formatDateForAPI(form.value.eventEndTime),
    isDisabled: form.value.isDisabled,
    isVotingDisabled: form.value.isVotingDisabled,
    votingStartTime: formatDateForAPI(form.value.votingStartTime),
    votingEndTime: formatDateForAPI(form.value.votingEndTime),
    discardedSongs: form.value.discardedSongs,
    discardedCategories: form.value.discardedCategories,
    pinnedSongs: form.value.pinnedSongs,
    notes: form.value.notes.trim() || null,
    theme: form.value.theme || "light",
    concertsStyle: form.value.concertsStyle || CONCERTS_STYLES.DEFAULT,
    signUpMode: form.value.signUpMode || "none",
    defaultLocale: form.value.defaultLocale || DEFAULT_LOCALE,
    enabledLocales: form.value.enabledLocales || [],
    autoQueueNext: form.value.autoQueueNext || false,
    setlistEnabled: form.value.setlistEnabled || false,
    setlistDurations: form.value.setlistEnabled
      ? form.value.setlistDurations.map((d) => parseInt(d) || 0).filter((d) => d > 0)
      : []
  };

  if (isPrevoting(form.value.eventType) || isKaraoke(form.value.eventType)) {
    // Both prevoting and karaoke derive event start/end from the session (voting) times
    // since the Event Start/End pickers are hidden for these types.
    payload.eventStartTime = formatDateForAPI(form.value.votingStartTime);
    payload.eventEndTime = formatDateForAPI(form.value.votingEndTime);
    payload.setlistEnabled = false;
    payload.setlistDurations = [];
    payload.autoQueueNext = false;
  }
  if (isKaraoke(form.value.eventType)) {
    payload.pinnedSongs = [];
  }

  return payload;
}

const markPristine = () => {
  pristineSnapshot.value = JSON.stringify(form.value);
};

const isDirty = () => JSON.stringify(form.value) !== pristineSnapshot.value;

const addSetDuration = () => {
  form.value.setlistDurations.push(45);
};

const removeSetDuration = (index) => {
  if (form.value.setlistDurations.length > 1) {
    form.value.setlistDurations.splice(index, 1);
  }
};

const updateSetDuration = (index, value) => {
  form.value.setlistDurations[index] = parseInt(value) || 0;
};

const fetchCategories = async () => {
  if (categoriesLoading.value || categories.value.length > 0) return;
  try {
    categoriesLoading.value = true;
    const response = await api.get("/api/categories");
    if (response.data.success) {
      categories.value = response.data.data.categories || [];
    }
  } catch (err) {
    console.error("Failed to fetch categories:", err);
  } finally {
    categoriesLoading.value = false;
  }
};

const fetchSongs = async () => {
  if (songsLoading.value || songs.value.length > 0) return;
  try {
    songsLoading.value = true;
    const response = await api.get("/api/songs");
    if (response.data.success) {
      songs.value = response.data.data.songs || [];
    }
  } catch (err) {
    console.error("Failed to fetch songs:", err);
  } finally {
    songsLoading.value = false;
  }
};

const loadEvent = async () => {
  if (isNew.value) {
    markPristine();
    return;
  }

  loading.value = true;
  try {
    const response = await api.get(`/api/events/${eventId.value}`);
    if (response.data.success) {
      form.value = formFromEvent(response.data.data.event);
      markPristine();
    } else {
      loadError.value = response.data.message || "Failed to load event";
    }
  } catch (err) {
    console.error("Failed to load event:", err);
    loadError.value = err.response?.data?.message || "Failed to load event";
  } finally {
    loading.value = false;
  }
};

const handleSave = async () => {
  serverError.value = "";
  errors.value = validateEventForm();

  const firstInvalid = sections.value.find((section) => sectionErrorCounts.value[section.id]);
  if (firstInvalid) {
    goToSection(firstInvalid.id);
    window.scrollTo?.({ top: 0, behavior: "smooth" });
    return;
  }

  try {
    saving.value = true;
    const response = isNew.value
      ? await api.post("/api/events", buildPayload())
      : await api.put(`/api/events/${eventId.value}`, buildPayload());

    if (!response.data.success) {
      serverError.value = response.data.message || "Failed to save event";
      return;
    }

    toast.add({
      severity: "success",
      summary: "Success",
      detail: isNew.value ? "Event created successfully" : "Event updated successfully",
      life: 3000
    });
    leavingAfterSave.value = true;
    router.push("/events");
  } catch (err) {
    console.error("Failed to save event:", err);
    serverError.value =
      err.response?.data?.message ||
      err.response?.data?.errors?.join(", ") ||
      "Failed to save event";
  } finally {
    saving.value = false;
  }
};

const goBackToList = () => {
  if (window.history.state?.back) {
    router.back();
    return;
  }
  router.push("/events");
};

const handleCancel = () => {
  goBackToList();
};

onBeforeRouteLeave(() => {
  if (leavingAfterSave.value || !isDirty()) return true;

  return new Promise((resolve) => {
    confirm.require({
      message: isNew.value
        ? "Are you sure you want to cancel creating this event? Any unsaved changes will be lost."
        : "Are you sure you want to leave? Any unsaved changes will be lost.",
      header: isNew.value ? "Cancel Creating Event" : "Discard Changes",
      icon: "pi pi-exclamation-triangle",
      rejectProps: { label: "No, Continue Editing", severity: "secondary", outlined: true },
      acceptProps: { label: "Yes, Discard", severity: "danger" },
      accept: () => resolve(true),
      reject: () => resolve(false),
      onHide: () => resolve(false)
    });
  });
});

watch(
  () => [
    songs.value,
    ...(form.value.discardedSongs || []),
    ...(form.value.discardedCategories || [])
  ],
  prunePinnedSongs
);

watch(
  () => form.value.enabledLocales,
  (enabledLocales) => {
    if (enabledLocales.length === 0) return;
    if (!enabledLocales.includes(form.value.defaultLocale)) {
      form.value.defaultLocale = enabledLocales[0];
    }
  },
  { deep: true }
);

// Once a save attempt has surfaced errors, keep them in sync as fields are fixed.
watch(
  form,
  () => {
    if (Object.keys(errors.value).length > 0) {
      errors.value = validateEventForm();
    }
  },
  { deep: true }
);

onMounted(() => {
  loadEvent();
  fetchSongs();
  fetchCategories();
});
</script>

<style scoped>
/* Style ToggleSwitch ON state (when event is disabled) to be red */
:deep(.event-disabled-toggle.p-toggleswitch-checked .p-toggleswitch-slider) {
  background-color: #ef4444 !important; /* red-500 */
}

:deep(.event-disabled-toggle .p-toggleswitch-slider:before) {
  background-color: white;
}

/* Style ToggleSwitch ON state (when voting is disabled) to be orange */
:deep(.event-voting-disabled-toggle.p-toggleswitch-checked .p-toggleswitch-slider) {
  background-color: #f97316 !important; /* orange-500 */
}

:deep(.event-voting-disabled-toggle .p-toggleswitch-slider:before) {
  background-color: white;
}

/* Style ToggleSwitch ON state (when auto-queue is enabled) to be green */
:deep(.event-auto-queue-toggle.p-toggleswitch-checked .p-toggleswitch-slider) {
  background-color: #22c55e !important; /* green-500 */
}

:deep(.event-auto-queue-toggle .p-toggleswitch-slider:before) {
  background-color: white;
}

/* Style ToggleSwitch ON state (when setlist is enabled) to be green */
:deep(.event-setlist-toggle.p-toggleswitch-checked .p-toggleswitch-slider) {
  background-color: #22c55e !important; /* green-500 */
}

:deep(.event-setlist-toggle .p-toggleswitch-slider:before) {
  background-color: white;
}
</style>
